<template>
  <div :class="{ 'h-full': !showInfo }">
    <div
      ref="mapEl"
      :class="showInfo ? 'shadow-lg mb-12' : ''"
      :style="{ height: showInfo ? '300px' : '100%', width: '100%' }"
    ></div>
    <template v-if="showInfo">
      <div class="font-bold">{{ location }}</div>
      <div>{{ address }}</div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'

const props = defineProps({
  latitude: { type: Number, required: true },
  longitude: { type: Number, required: true },
  address: { type: String, default: '' },
  radius: { type: Number, required: false },
  draggable: { type: Boolean, default: true },
  showInfo: { type: Boolean, default: true },
  points: { type: Array, default: () => [] },
  selectedPointId: { type: [Number, String], default: null },
  fitToPoints: { type: Boolean, default: true },
  /** `'roadmap' | 'satellite'` — a vista de satélite é o que revela o prédio sob o pino. */
  mapType: { type: String, default: 'roadmap' },
  zoom: { type: Number, default: 15 },
  /** Desliga o círculo mesmo com `radius` (mapa só de conferência). */
  showRadiusCircle: { type: Boolean, default: true }
})
const emit = defineEmits(['marker-dragged', 'point-click'])

const mapEl = ref(null)
let map = null
let marker = null
let circle = null
let pointMarkers = []

function pointIcon(point, selected) {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: selected ? 9 : 6,
    fillColor: point.color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: selected ? 2.5 : 1.5
  }
}

function clearPoints() {
  pointMarkers.forEach((m) => {
    google.maps.event.clearInstanceListeners(m)
    m.setMap(null)
  })
  pointMarkers = []
}

function renderPoints() {
  if (!map) return
  clearPoints()
  const points = props.points || []
  points.forEach((point) => {
    const m = new google.maps.Marker({
      position: { lat: point.lat, lng: point.lng },
      map,
      title: point.label || '',
      icon: pointIcon(point, point.id === props.selectedPointId),
      zIndex: point.id === props.selectedPointId ? 20 : 10
    })
    m.addListener('click', () => emit('point-click', point.id))
    pointMarkers.push(m)
  })
  if (props.fitToPoints && points.length) {
    const bounds = new google.maps.LatLngBounds()
    bounds.extend({ lat: props.latitude, lng: props.longitude })
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
    if (circle) {
      const cb = circle.getBounds()
      if (cb) bounds.union(cb)
    }
    map.fitBounds(bounds, 50)
  }
}

const location = computed(() => `Latitude: ${props.latitude} — Longitude: ${props.longitude}`)

onMounted(async () => {
  await nextTick()

  const center = { lat: props.latitude, lng: props.longitude }
  map = new google.maps.Map(mapEl.value, {
    center,
    zoom: props.zoom,
    mapTypeId: props.mapType
  })

  marker = new google.maps.Marker({
    position: center,
    map,
    draggable: props.draggable
  })

  if (props.showRadiusCircle && props.radius != null && props.radius > 0) {
    circle = new google.maps.Circle({
      map,
      center,
      radius: props.radius,
      strokeColor: '#007bff',
      strokeOpacity: 0.6,
      strokeWeight: 2,
      fillColor: '#007bff',
      fillOpacity: 0.2
    })

    const bounds = circle.getBounds()
    if (bounds) {
      map.fitBounds(bounds, 50)
    }
  }

  if (props.draggable) {
    marker.addListener('dragend', (event) => {
      const newCenter = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }
      map.panTo(newCenter)

      if (circle) {
        circle.setCenter(newCenter)
        const bounds = circle.getBounds()
        if (bounds) map.fitBounds(bounds, 50)
      }
      emit('marker-dragged', newCenter)
    })
  }

  renderPoints()
})

onUnmounted(() => {
  if (marker) {
    google.maps.event.clearInstanceListeners(marker)
    marker.setMap(null)
  }
  if (circle) {
    circle.setMap(null)
  }
  clearPoints()
  map = null
  marker = null
  circle = null
})

watch(
  () => [props.latitude, props.longitude],
  ([newLat, newLng]) => {
    if (!map || !marker) return
    const newCenter = { lat: newLat, lng: newLng }
    map.panTo(newCenter)
    marker.setPosition(newCenter)
    if (circle) {
      circle.setCenter(newCenter)
      const bounds = circle.getBounds()
      if (bounds) map.fitBounds(bounds, 50)
    }
  }
)

watch(
  () => props.radius,
  (newRadius) => {
    if (!props.showRadiusCircle || newRadius == null) return

    const center = { lat: props.latitude, lng: props.longitude }

    if (!circle) {
      circle = new google.maps.Circle({
        map,
        center,
        radius: newRadius,
        strokeColor: '#007bff',
        strokeOpacity: 0.6,
        strokeWeight: 2,
        fillColor: '#007bff',
        fillOpacity: 0.2
      })
    } else {
      circle.setRadius(newRadius)
    }

    const bounds = circle.getBounds()
    if (bounds) map.fitBounds(bounds, 50)
  }
)

watch(
  () => props.mapType,
  (newMapType) => {
    if (map) map.setMapTypeId(newMapType)
  }
)

watch(
  () => props.points,
  () => renderPoints(),
  { deep: true }
)

watch(
  () => props.selectedPointId,
  (id) => {
    if (!map) return
    const points = props.points || []
    pointMarkers.forEach((m, i) => {
      const point = points[i]
      if (point) m.setIcon(pointIcon(point, point.id === id))
    })
    const selected = points.find((p) => p.id === id)
    if (selected) map.panTo({ lat: selected.lat, lng: selected.lng })
  }
)
</script>
